import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import CheckIcon from "@material-ui/icons/Check";
import ReportProblemOutlinedIcon from "@material-ui/icons/ReportProblemOutlined";
import EpeService from "../../../../services/EpeService";
import { getErrorMessage } from "../../../../helpers/functions";
import COLORS from "../../../../assets/Colors";
import theme from "../../../../assets/theme";
import EpeDialog, {
  DialogNotice,
  DialogPrimaryButton,
  DialogSecondaryButton,
  DialogSpinner,
} from "./EpeDialog";

// "Start new hearing session": every JustCue screen of the case goes back to the splash (POST
// epe/cases/:caseId/session/new). Cancel has the first focus, because ending a session is a deliberate act; while
// the request is out, Cancel, Esc, the X and the backdrop wait for its answer, so it is never lost.

const NETWORK_TEXT =
  "JustCue could not reach the server. Check the connection and try again.";

const secondsText = (seconds) =>
  seconds === 1 ? "1 second" : `${seconds} seconds`;

/**
 * The words for a refusal. The backend's own message comes first; the fallbacks cover an answer without one (the
 * API wrapper keeps only the response body, so they go by JustCue's code and retryAfter rather than the status).
 */
const refusalText = (err) => {
  if (!err || typeof err !== "object" || err instanceof Error) {
    return NETWORK_TEXT;
  }
  if (typeof err.message === "string" && err.message.trim()) {
    return err.message;
  }
  const retryAfter = Math.ceil(Number(err.retryAfter));
  if (err.code === "TOO_SOON" || retryAfter > 0) {
    return `A new hearing session started moments ago. Try again in ${
      retryAfter > 0 ? secondsText(retryAfter) : "a few seconds"
    }.`;
  }
  if (err.code === "SESSION_CHANGED") {
    return "Someone else has just started a new hearing session. Check what the room sees before starting another.";
  }
  if (err.code === "FORBIDDEN") {
    return "Only a case manager or the EPE operator in control can start a new hearing session.";
  }
  return getErrorMessage(err);
};

export default function StartHearingSessionModal({
  caseId,
  onClose,
  onStarted,
}) {
  // confirm → busy → done, or back to confirm with the refusal under the question
  const [phase, setPhase] = useState("confirm");
  const [error, setError] = useState(null);
  const cancelRef = useRef(null);
  const doneRef = useRef(null);
  // Focus after the next render: Done once started, Cancel after a refusal.
  const focusNext = useRef(null);
  // A request is out (a second click before the busy state renders sends nothing).
  const sending = useRef(false);
  const alive = useRef(true);
  const busy = phase === "busy";

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => {
    const el =
      focusNext.current === "done"
        ? doneRef.current
        : focusNext.current === "cancel"
        ? cancelRef.current
        : null;
    focusNext.current = null;
    if (el) el.focus();
  });

  const start = async () => {
    if (sending.current) return;
    sending.current = true;
    setError(null);
    setPhase("busy");
    try {
      const res = await EpeService.startNewSession(caseId);
      if (!alive.current) return;
      focusNext.current = "done";
      setPhase("done");
      if (onStarted) onStarted(res?.startedAt);
    } catch (err) {
      if (!alive.current) return;
      focusNext.current = "cancel";
      setError(refusalText(err));
      setPhase("confirm");
    } finally {
      sending.current = false;
    }
  };

  return (
    <EpeDialog
      id="epe-new-session"
      title="New hearing session"
      description="For the next hearing, or another rehearsal: every screen of this case starts again from the splash."
      onClose={onClose}
      busy={busy}
      initialFocusRef={cancelRef}
      maxWidth={520}
      footer={
        phase === "done" ? (
          <DialogPrimaryButton ref={doneRef} type="button" onClick={onClose}>
            Done
          </DialogPrimaryButton>
        ) : (
          <>
            <DialogSecondaryButton
              ref={cancelRef}
              type="button"
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </DialogSecondaryButton>
            <DialogPrimaryButton
              type="button"
              onClick={start}
              aria-disabled={busy || undefined}
            >
              {busy ? (
                <>
                  <DialogSpinner />
                  Starting…
                </>
              ) : (
                "Start new session"
              )}
            </DialogPrimaryButton>
          </>
        )
      }
    >
      {phase !== "done" && (
        <ConfirmText>
          Every screen goes back to the splash. The presented documents, the
          undo history and the hearing log start again; the log so far is
          kept.
        </ConfirmText>
      )}

      {error && phase !== "done" && (
        <DialogNotice role="alert">
          <NoticeIcon aria-hidden="true">
            <ReportProblemOutlinedIcon style={{ fontSize: 16 }} />
          </NoticeIcon>
          <span>{error}</span>
        </DialogNotice>
      )}

      {/* Always in the page, so the screen reader hears the start as soon as the words arrive. */}
      <SuccessLine role="status">
        {phase === "done" && (
          <>
            <SuccessIcon aria-hidden="true">
              <CheckIcon style={{ fontSize: 18 }} />
            </SuccessIcon>
            New hearing session started. Every screen is back to the splash.
          </>
        )}
      </SuccessLine>
    </EpeDialog>
  );
}

const ConfirmText = styled.p`
  margin: 0;
  padding: 12px 14px;
  border-radius: 10px;
  border-left: 4px solid #f6b436;
  background: #f8fafc;
  color: ${COLORS.COLOR_DARK};
  font-size: 13.5px;
  line-height: 1.6;
  font-family: ${theme.fonts.primaryFontRegular};
`;

const NoticeIcon = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  margin-top: 2px;
  color: #c98a12;
`;

const SuccessLine = styled.p`
  margin: 0;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: ${COLORS.COLOR_DARK};
  font-size: 13.5px;
  line-height: 1.6;
  font-family: ${theme.fonts.primaryFontSemiBold};

  /* Empty, it stays in the accessibility tree (a live region) but takes no room. */
  &:empty {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
  }
`;

const SuccessIcon = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  margin-top: 1px;
  color: ${COLORS.BTN_GREEN};
`;
